import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { cheapestMonthlyPlan } from '../../components/pages/CheckoutPage/planPricing';
import { NATIVE_METHOD_PAYMENT_TYPE } from '../../components/pages/PaymentPage/NativePaymentButton';
import { isUnlimited } from '../../components/pages/ProfileV2/accountUsage';
import PaymentApi from '../../service/Api/paymentApi';
import { gaEvents } from '../../utils/analytics';

const initialState = {
    payments: {
        success: true,
        error: null,
        loading: false,
        plansList: [],
        productsList: []
    },
    history: {
        success: true,
        error: null,
        loading: false,
        transactionsList: [],
        hasMore: true,
        currentPage: 1,
        reloading: false
    },
    // HORO-10538. `data: null` is a real answer — an account with no card on
    // file — so `loaded` is what separates "we asked and there is none" from
    // "we haven't asked yet". Without it the view flashes its empty state on
    // every mount before the request resolves.
    method: {
        loading: false,
        loaded: false,
        error: null,
        data: null,
        // Resolved server-side, not derived from the plan catalogue — an
        // active recurring offer or a grandfathered plan makes the catalogue
        // price the wrong number. Null when nothing will be charged.
        nextCharge: null
    },
    currentPaymentData: {}
};

export const fetchPlans = createAsyncThunk('payment/fetchPlan', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.getPlansList();
        const plans = data.data;
        plans.data.map((plan) => ({
            ...plan,
            isOffer: !!plan.basePrice
        }));
        return plans;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const fetchProducts = createAsyncThunk('payment/fetchProduct', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.getProductsList();
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const fetchHistory = createAsyncThunk('payment/fetchHistory', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.getPaymentsHistory(params.page);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const fetchPaymentMethod = createAsyncThunk('payment/fetchMethod', async (params, { rejectWithValue }) => {
    try {
        // `RequestsApi.request` resolves with the axios RESPONSE, not the body,
        // so the payload is under `.data` — every sibling thunk unwraps it the
        // same way. Reading `response.method` instead silently yields undefined
        // for a perfectly good 200, which renders as "No payment method on
        // file" for a subscriber who has one.
        const response = await PaymentApi.getPaymentMethod();
        const body = response?.data;

        // `method` and `nextCharge` are top-level in the body, beside `success`,
        // and either can legitimately be null. Coalesced here so the reducer
        // never has to think about undefined.
        return { method: body?.method ?? null, nextCharge: body?.nextCharge ?? null };
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const createTrial = createAsyncThunk('payment/createTrial', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.createPaymentTrial(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createPlan = createAsyncThunk('payment/createPlan', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.createPaymentPlan(params.id, params.data);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createProduct = createAsyncThunk('payment/createProduct', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.createPaymentProduct(params.id, params.data);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createTrialSession = createAsyncThunk('payment/createTrialSession', async (params, { rejectWithValue }) => {
    try {
        const data = await PaymentApi.createSessionTrial(params);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const createPlanSession = createAsyncThunk('payment/createPlanSession', async (params, { rejectWithValue }) => {
    try {
        const data = await PaymentApi.createSessionPlan(params.id, params.data);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const createProductSession = createAsyncThunk('payment/createProductSession', async (params, { rejectWithValue }) => {
    try {
        const data = await PaymentApi.createSessionProduct(params.id, params.data);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const createNativeMethodToken = createAsyncThunk('payment/createGooglePayToken', async (params, { rejectedWithValue }) => {
    try {
        const data =
            params.type === NATIVE_METHOD_PAYMENT_TYPE.GOOGLE
                ? await PaymentApi.createGooglePayToken(params.token)
                : await PaymentApi.createApplePayToken(params.token);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

/**
 * HORO-10539: deferred plan switching.
 *
 * The backend's rejections carry a machine-readable `code` in the error body
 * ("already_on_plan", "no_active_plan", "store_billed"...), which is what the
 * views key their copy on — a prose message would break the moment it changed.
 * `rejectWithValue` is spelled correctly here on purpose: several older thunks
 * in this file destructure `rejectedWithValue`, which is undefined, so their
 * rejection path throws instead of rejecting cleanly.
 */
const switchErrorPayload = (error) => ({
    code: error?.response?.data?.data?.code || error?.response?.data?.code || null,
    status: error?.response?.status || 0
});

export const previewPlanSwitch = createAsyncThunk('payment/previewPlanSwitch', async (planId, { rejectWithValue }) => {
    try {
        const response = await PaymentApi.previewPlanSwitch(planId);
        return response?.data?.data;
    } catch (error) {
        return rejectWithValue(switchErrorPayload(error));
    }
});

export const schedulePlanSwitch = createAsyncThunk('payment/schedulePlanSwitch', async (planId, { rejectWithValue }) => {
    try {
        const response = await PaymentApi.schedulePlanSwitch(planId);
        return response?.data?.data;
    } catch (error) {
        return rejectWithValue(switchErrorPayload(error));
    }
});

export const cancelPendingPlanSwitch = createAsyncThunk('payment/cancelPendingPlanSwitch', async (params, { rejectWithValue }) => {
    try {
        const response = await PaymentApi.cancelPendingPlanSwitch();
        return response?.data?.data;
    } catch (error) {
        return rejectWithValue(switchErrorPayload(error));
    }
});

export const cancelSubscription = createAsyncThunk('payment/cancelSubscription', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.cancelSubscription(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

// HORO-10545: the two surveys sharing one store. Kept beside the thunk so the
// values the client sends and the values the backend validates have one origin.
export const SURVEY_TYPE = {
    SUBSCRIPTION_CANCEL: 'subscription_cancel',
    ACCOUNT_DELETION: 'account_deletion'
};

/**
 * Record why someone cancelled or deleted. Best-effort by design: it resolves
 * rather than rejects on failure, because the destructive action the customer
 * actually asked for must not be blocked by a survey write.
 *
 * Best-effort still has to be observable. Swallowing the error silently meant a
 * wrong endpoint path looked exactly like a working one — the cancellation
 * succeeded, no row was written, and nothing anywhere said so. The failure is
 * reported to analytics so it can be seen without ever reaching the customer.
 */
export const submitFeedbackSurvey = createAsyncThunk('payment/submitFeedbackSurvey', async (params) => {
    try {
        const data = await PaymentApi.submitFeedbackSurvey(params);
        return data.data;
    } catch (error) {
        gaEvents.apiError({
            status: error?.response?.status || 0,
            method: 'POST',
            path: '/feedback-survey',
            code: params?.surveyType || ''
        });
        return { success: false };
    }
});

export const getPaymentsState = (store) => store.payments.payments;
export const getPaymentsLoadingState = (store) => store.payments.payments.loading;

export const getTrialPlan = (store) => store.payments.payments?.plansList.find((plan) => plan.isTrial);

// Every non-trial plan, including one the customer holds that is no longer on
// sale. Used to DESCRIBE a plan — Plan Management resolves the customer's own
// card from this — never to offer one.
export const getAllSubscriptionPlans = (store) => (store.payments.payments?.plansList || []).filter((plan) => !plan.isTrial);

// Every non-trial plan the customer could actually choose between. The trial is
// excluded because it isn't an alternative to a subscription, it's the way into
// one. Drives whether "Change plan" is worth offering: with a single plan on the
// account that link leads to a page showing the plan the customer is already
// buying, so it's a dead end rather than a choice.
//
// HORO-10539: `isActive: false` is excluded. The catalogue now also returns the
// plan a grandfathered subscriber is on, so that they can be told what they
// have — offering it for sale is a different thing, and it is refused
// server-side too.
export const getSubscriptionPlans = (store) => getAllSubscriptionPlans(store).filter((plan) => plan.isActive !== false);

/**
 * HORO-10554: the plan to QUOTE A PRICE FROM, never the plan to charge.
 *
 * This replaces the old `getSubscriptionPlan`, which was
 * `plansList.find((plan) => !plan.isTrial)` — the first non-trial plan in
 * whatever order the API returned it. That was harmless while one plan was on
 * sale and became a wrong-charge bug the moment a second one was, because the
 * purchase paths took their `paymentId` from it.
 *
 * Two things changed. Charging no longer has a selector at all: a purchase
 * carries the plan the customer chose, or checkout asks. And what remains here
 * is display-only and deterministic — the lowest per-month equivalent, which is
 * the "from $4.99/mo" figure the upsell copy means, rather than an accident of
 * insertion order. Ties keep the earlier plan so the choice is stable.
 *
 * Callers must not pass the result to a payment. It answers "what is the
 * cheapest we can advertise", not "what is this customer buying".
 *
 * Shares `cheapestMonthlyPlan` with the profile's "from $X/mo" upsell rather
 * than reimplementing it, so the price the upsell advertises and the price
 * anything else quotes cannot disagree.
 */
export const getFeaturedSubscriptionPlan = (store) => cheapestMonthlyPlan(getSubscriptionPlans(store));

// HORO-10449: two message bundles run at once, segmented by audience —
// subscribers see the 200-message bundle, everyone on the free plan sees the
// 20-message one. An active trial counts as a subscriber: `isFree` is false
// for its duration.
const SUBSCRIBER_BUNDLE_MESSAGE_COUNT = 200;
const NON_SUBSCRIBER_BUNDLE_MESSAGE_COUNT = 20;

export const getProductForUserSegment = (store) => {
    const { productsList } = store.payments.payments;

    // Nothing to sell someone with no limit. The bundle predates the Subscriber
    // tiers, when every subscriber was capped at 200 and buying more was the
    // only way up; "Unlock 200 messages for $1.99" offered to an unlimited plan
    // sells something that cannot be used. Returning null here withdraws it from
    // every surface at once — the chat popup, both account screens and the
    // paywall button — rather than each one testing the plan for itself.
    if (isUnlimited(store.auth.user.limits)) return null;

    // Defaults cover guests (no plan data yet) — treat them as a future
    // non-subscriber, since that's what they'd actually get upon registering.
    // HORO-10680: `isFree` alone. This read `isFree && !isTrial`, but
    // `plan.isTrial` means "has ever started a trial" and nothing clears it —
    // planCtas.js depends on that to stop re-offering a used trial, and
    // existTrial() on the backend uses it to reject a second one. So once a
    // trial ended, the customer was read as a subscriber forever and sold 200
    // messages at the 20-message price. `isFree` is false for the whole of an
    // active trial, so mid-trial customers still get the subscriber bundle.
    const { isFree = true } = store.auth.user.plan || {};
    const targetCount = isFree ? NON_SUBSCRIBER_BUNDLE_MESSAGE_COUNT : SUBSCRIBER_BUNDLE_MESSAGE_COUNT;

    // HORO-10680: no match means no offer. This used to fall back to
    // productsList[0], which is the 200-message bundle in every environment, so
    // an unmatched segment was served the larger bundle. The '20 messages'
    // product has been retired and reactivated twice already (see the backend's
    // bundle migrations), and either toggle drops every free user onto it.
    return productsList.find((product) => product.limits?.messages?.count === targetCount) || null;
};

/**
 * HORO-10703: the bundle the FREE plan can buy, whoever is looking.
 *
 * Deliberately not `getProductForUserSegment`, which answers "what should we
 * sell *this viewer*". The /plans Free column describes the free plan as a
 * product, so a legacy subscriber reading it would otherwise see their own
 * 200-message bundle advertised under a plan that only ever gets the 20.
 */
export const getFreeTierMessagePack = (store) =>
    (store.payments.payments.productsList || []).find(
        (product) => product.limits?.messages?.count === NON_SUBSCRIBER_BUNDLE_MESSAGE_COUNT
    ) || null;

export const getPaymentsHistoryState = (store) => store.payments.history;
export const getHistoryLoadingState = (store) => store.payments.history.loading;
export const getTransactionsList = (store) => store.payments.history.transactionsList;

export const getPaymentMethodState = (store) => store.payments.method;

export const getCurrentPaymentData = (store) => store.payments.currentPaymentData;

const paymentsSlice = createSlice({
    name: 'payments',
    initialState,
    reducers: {
        clearPaymentsError(store) {
            store.payments.error = null;
        },
        clearHistoryError(store) {
            store.history.error = null;
        },
        setCurrentPaymentData(store, { payload }) {
            store.currentPaymentData = {
                ...payload
            };
        },
        /**
         * Apply an offer the backend granted to the plan it was granted for.
         *
         * HORO-10554: `planId` is required. This used to write the payload onto
         * every non-trial plan, which was indistinguishable from correct while
         * one plan existed. With two, granting the monthly plan's $4.99 intro
         * offer also stamped $4.99 onto the annual plan — the customer would
         * have been shown, and quoted, a price for a plan nobody had discounted.
         * An unmatched id changes nothing, which is the safe outcome.
         */
        setSubscriptionPlan(store, { payload: { planId, ...planFields } }) {
            store.payments.plansList = store.payments.plansList.map((plan) => {
                return plan.id === planId ? { ...plan, ...planFields } : plan;
            });
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchPlans.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(fetchPlans.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    plansList: payload?.data
                };
            })
            .addCase(fetchPlans.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(fetchProducts.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(fetchProducts.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    productsList: payload?.data
                };
            })
            .addCase(fetchProducts.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(fetchHistory.pending, (store, { meta: { arg } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.history = {
                    ...store.history,
                    loading: true,
                    error: null,
                    reloading
                };
            })
            .addCase(fetchHistory.fulfilled, (store, { payload }) => {
                const reloading = store.history.reloading;
                store.history = {
                    ...store.history,
                    loading: false,
                    reloading: false,
                    success: payload?.success,
                    transactionsList: reloading ? [...payload.transaction] : [...store.history.transactionsList, ...payload.transaction],
                    hasMore: payload?.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1
                };
            })
            .addCase(fetchHistory.rejected, (store, { payload }) => {
                store.history = {
                    ...store.history,
                    loading: false,
                    reloading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(fetchPaymentMethod.pending, (store) => {
                store.method = { ...store.method, loading: true, error: null };
            })
            .addCase(fetchPaymentMethod.fulfilled, (store, { payload }) => {
                store.method = { loading: false, loaded: true, error: null, data: payload.method, nextCharge: payload.nextCharge };
            })
            .addCase(fetchPaymentMethod.rejected, (store, { payload }) => {
                // `loaded` stays false on failure. A failed request is not
                // evidence that the account has no card, and treating it as
                // such would tell a paying subscriber they have no payment
                // method — the view shows an error instead.
                store.method = { ...store.method, loading: false, error: payload?.error || true, data: null, nextCharge: null };
            })
            .addCase(createTrial.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createTrial.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createTrial.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createPlan.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createPlan.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createPlan.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createProduct.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createProduct.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createProduct.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createNativeMethodToken.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createNativeMethodToken.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createNativeMethodToken.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(cancelSubscription.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(cancelSubscription.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(cancelSubscription.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            });
    }
});

export const paymentsReducer = paymentsSlice.reducer;

export const { clearPaymentsError, clearHistoryError, setCurrentPaymentData, setSubscriptionPlan } = paymentsSlice.actions;
